import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fetchTour } from "@/lib/api";
import { IMAGES } from "@/lib/data";

const STATUS_STYLES = {
  "on-sale": { label: "On Sale", cls: "text-cyan-glow border-cyan-neon/40" },
  "low": { label: "Low Stock", cls: "text-gold border-gold/40" },
  "sold-out": { label: "Sold Out", cls: "text-slate-500 border-white/10" },
  "tba": { label: "TBA", cls: "text-gold border-gold/40" },
};

function formatDate(iso) {
  if (!iso || iso === "TBA") return { m: "TBA", day: "—", year: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { m: "TBA", day: "—", year: "" };
  const m = d.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const day = d.getDate().toString().padStart(2, "0");
  return { m, day, year: d.getFullYear() };
}

export const Tour = () => {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTour()
      .then(setDates)
      .catch(() => setDates([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="tour" data-testid="tour-section" className="relative py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.tourBg} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950 via-ink-950/85 to-ink-950" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-magenta-glow mb-3">
              ◢ World Tour 2027
            </div>
            <h2 className="font-display font-black uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              The <span className="text-shimmer-gold">Tsunami</span> Tour
            </h2>
          </div>
          <p className="font-serif italic text-slate-400 text-lg max-w-sm">
            Forty-seven cities. One unstoppable tsunami.
          </p>
        </div>

        <div data-testid="tour-list" className="divide-y divide-white/10 border-y border-white/10">
          {loading && (
            <div className="py-16 text-center text-slate-500 text-sm tracking-[0.3em] uppercase">
              Loading the tsunami…
            </div>
          )}
          {!loading &&
            dates.map((t, i) => {
              const { m, day, year } = formatDate(t.date);
              const st = STATUS_STYLES[t.status] || STATUS_STYLES["on-sale"];
              const soldOut = t.status === "sold-out";
              const tba = t.status === "tba" || t.date === "TBA";
              return (
                <motion.a
                  key={t.id}
                  href={tba ? undefined : t.ticket_url}
                  data-testid={`tour-row-${t.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  className={`group relative grid grid-cols-12 items-center gap-4 py-6 md:py-8 px-4 md:px-6 transition-all ${
                    soldOut
                      ? "opacity-50"
                      : tba
                      ? "cursor-default"
                      : "hover:bg-gradient-to-r hover:from-magenta-hot/10 hover:via-cyan-neon/5 hover:to-transparent"
                  }`}
                >
                  <div className="col-span-3 md:col-span-2 flex items-baseline gap-2">
                    {tba ? (
                      <span className="font-display font-black text-3xl md:text-5xl tracking-tight text-gold">
                        TBA
                      </span>
                    ) : (
                      <>
                        <span className="font-display font-black text-3xl md:text-5xl tracking-tight">{day}</span>
                        <div className="flex flex-col">
                          <span className="font-mono text-[10px] text-gold">{m}</span>
                          <span className="font-mono text-[10px] text-slate-500">{year}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="col-span-5 md:col-span-4">
                    <div className="font-display font-bold text-xl md:text-3xl uppercase tracking-tight group-hover:text-white">
                      {t.city}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{t.country}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3 text-sm md:text-base text-slate-300 font-light">
                    {tba ? <span className="text-slate-500 italic">Venue TBA</span> : t.venue}
                  </div>
                  <div className="col-span-8 md:col-span-2 flex justify-start md:justify-center">
                    <span className={`text-[10px] tracking-[0.3em] uppercase px-3 py-1 border ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                  <div className="col-span-4 md:col-span-1 flex justify-end">
                    <span
                      className={`w-11 h-11 rounded-full flex items-center justify-center border transition-all ${
                        soldOut || tba
                          ? "border-white/10 text-slate-600"
                          : "border-white/20 text-slate-300 group-hover:bg-gold group-hover:border-gold group-hover:text-ink-950"
                      }`}
                    >
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                </motion.a>
              );
            })}
        </div>
      </div>
    </section>
  );
};
